import { Edit } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { Contact } from "@repo/server-api";
import { useTranslation } from "react-i18next";
import { useMemo, useRef } from "react";

import { useBccRecipientLists } from "../hooks/useBccRecipientLists";
import { BccRecipientListModel } from "../types/BccRecipientListTypes";
import { DialogBccRecipientList, DialogBccRecipientListHandle } from "./DialogBccRecipientList";

export type BccRecipientListsProps = Readonly<{
	contact: Contact | null;
	getContact: () => void;
}>;

export function BccRecipientLists(props: BccRecipientListsProps) {
	const { t } = useTranslation();
	const dialogRef = useRef<DialogBccRecipientListHandle>(null);

	// Use the hook for data management
	const { lists, isSaving, save } = useBccRecipientLists(props.contact?.id ?? null);

	const handleEdit = (row: BccRecipientListModel) => {
		dialogRef.current?.show(row);
	};

	const columns: GridColDef[] = useMemo(
		() => [
			{
				field: "type",
				headerName: t("type"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					const type = (params.row.type as string).toLowerCase();
					return t(`emailtype.${type}`, { ns: "enum" });
				},
			},
			{
				field: "recipients",
				headerName: t("bcc-recipients"),
				flex: 1,
				renderCell: (params: GridCellParams) => {
					const recipients = params.row.recipients as string[];
					return recipients.length > 0 ? recipients.join(", ") : "-";
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 150,
				renderCell: (params: GridCellParams) => {
					return (
						<div>
							<Button
								data-testid="edit-button"
								onClick={() => handleEdit(params.row as BccRecipientListModel)}
							>
								<Edit />
							</Button>
						</div>
					);
				},
			},
		],
		[t],
	);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<DataGridPremium
				getRowId={(row: BccRecipientListModel) => row.type}
				rows={lists}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				columns={columns as any}
				disableRowSelectionOnClick
				className="table"
				hideFooter={true}
			/>
			<DialogBccRecipientList
				ref={dialogRef}
				onSave={save}
				isSaving={isSaving}
			/>
		</Box>
	);
}
